"use client";

import React from "react";
import { Facebook, Instagram, Twitter, X, MapPin, Phone, Mail, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage, FormLabel } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
function FooterMenuLinks() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-24 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <nav className="flex flex-col gap-3 font-sans">
        <Link to="/" className="text-foreground hover:text-primary transition-colors w-fit text-sm tracking-wide">Home</Link>
        <Link to="/StudioServicesPage" className="text-foreground hover:text-primary transition-colors w-fit text-sm tracking-wide">Services</Link>
        <Link to="/ArtDecorShopPage" className="text-foreground hover:text-primary transition-colors w-fit text-sm tracking-wide">Shop</Link>
        <Link to="/FAQSupportPage" className="text-foreground hover:text-primary transition-colors w-fit text-sm tracking-wide">Support</Link>
      </nav>;
  }
  return <nav className="flex flex-col gap-3 font-sans">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-foreground hover:text-primary transition-colors w-fit text-sm tracking-wide">
          {item.label}
        </Link>)}
    </nav>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-4 border border-primary/20 bg-primary/5 rounded-md">
        <p className="text-primary text-sm font-sans">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-serif font-semibold text-foreground uppercase tracking-widest mb-1">Email</FormLabel>
            <div className="relative flex items-center">
              <FormControl>
                <Input placeholder="Enter your email" {...field} className="border-0 border-b border-primary rounded-md px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary shadow-md placeholder:text-foreground/50 text-foreground pr-16 h-10 transition-all font-sans" />
              </FormControl>
              <Button type="submit" disabled={isSubmitting} variant="ghost" className="absolute right-0 hover:bg-transparent text-primary hover:text-primary/80 h-8 px-0 text-sm font-bold tracking-wide font-serif">
                {isSubmitting ? <Loader className="size-4 animate-spin" /> : "Submit"}
              </Button>
            </div>
            <FormMessage className="text-xs text-destructive mt-1 font-sans" />
          </FormItem>} />
      {submitError && <p className="text-xs text-destructive font-sans">{submitError}</p>}
    </Form>;
}
export default function StudioCredits() {
  return <section id="studio-credits" className="relative pt-0 pb-16" style={{
    backgroundColor: 'transparent',
    color: 'var(--foreground)'
  }}>
      
      <div className="w-full h-px mb-16 opacity-80" style={{
      backgroundColor: 'transparent'
    }} />

      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          
          <div className="space-y-6">
            <div className="mb-6">
              <Link to="/">
                <WvcLogo className="h-7 w-auto" />
              </Link>
            </div>
            <p className="text-foreground text-sm leading-relaxed max-w-xs font-sans">
              Photo Glamour is a Riyadh studio providing photography, videography and large‑scale printing services. We bridge the gap between digital art and physical decor.
            </p>
            <div className="flex items-center gap-4 pt-2">
              <Link to="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-foreground/60 hover:text-white transition-colors">
                <Facebook className="size-5" strokeWidth={1.5} />
                <span className="sr-only">Facebook</span>
              </Link>
              <Link to="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-foreground/60 hover:text-white transition-colors">
                <Instagram className="size-5" strokeWidth={1.5} />
                <span className="sr-only">Instagram</span>
              </Link>
              <Link to="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-foreground/60 hover:text-white transition-colors">
                <Twitter className="size-5" strokeWidth={1.5} />
                <span className="sr-only">Twitter</span>
              </Link>
              <Link to="https://x.com" target="_blank" rel="noopener noreferrer" className="text-foreground/60 hover:text-white transition-colors">
                <X className="size-5" strokeWidth={1.5} />
                <span className="sr-only">X</span>
              </Link>
            </div>
          </div>

          <div className="space-y-6">
            <MenuProvider menu_id="24" className="w-full">
              <FooterMenuLinks />
            </MenuProvider>
          </div>

          <div className="space-y-6">
            <ul className="space-y-4 font-sans">
              <li className="flex items-start gap-3 text-sm text-foreground">
                <MapPin className="size-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                <span>Al Wadi, Riyadh, KSA</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-foreground">
                <Phone className="size-5 text-primary shrink-0" strokeWidth={1.5} />
                <div className="flex items-center gap-1">
                  <span className="text-foreground font-serif font-medium">Phone:</span>
                  <Link to="tel:+966531195122" className="hover:text-primary transition-colors">+966531195122</Link>
                </div>
              </li>
              <li className="flex items-center gap-3 text-sm text-foreground">
                <Mail className="size-5 text-primary shrink-0" strokeWidth={1.5} />
                <div className="flex items-center gap-1">
                  <span className="text-foreground font-serif font-medium">Email:</span>
                  <Link to="mailto:photoglamourweb@gmail.com" className="hover:text-primary transition-colors">photoglamourweb@gmail.com</Link>
                </div>
              </li>
            </ul>
          </div>

          <div className="space-y-6">
            <p className="text-sm text-foreground font-sans">
              Subscribe to receive updates on new collections and exclusive studio offers.
            </p>
            
            <FormProvider formId="newsletter-footer" sectionName="studio-credits" defaultValues={{
            email: ""
          }} submitText="Submit" successMessage="You have been subscribed." className="w-full">
              <NewsletterForm />
            </FormProvider>
          </div>

        </div>

        <div className="mt-16 pt-8 border-t border-border/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-foreground/60 font-sans">
          <p className="font-serif">&copy; {new Date().getFullYear()} Photo Glamour. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="#" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link to="#" className="hover:text-foreground transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </section>;
}